import { create } from 'zustand';

import { store as photosStore } from '@/state/use-photos/store';

/**
 * Local view/selection enums so this store does not depend on the
 * candidate-coupled `@repo/ui/state` barrel (which re-imports the app's candidate
 * store and creates a fragile circular dependency).
 */
export enum ViewMode {
  VIEW = 'view',
  SELECT = 'select',
}

export enum SelectionMode {
  SELECT_ALL = 'select-all',
  DESELECT_ALL = 'deselect-all',
}

/**
 * App-owned selection store for the moderation queue. Mirrors the talent
 * `use-candidate-selection` shape but lives in the app and reads the app's photos
 * store via a normal `@/state` path — deliberately NOT re-using the shared-package
 * selection store, which reaches back into `apps/web` and couples the two domains.
 */
export interface PhotoSelectionState {
  viewMode: ViewMode;
  selectedIds: string[];
  selectionMode: SelectionMode;
  selectId: (photoId: string) => void;
  unSelectId: (photoId: string) => void;
  selectAll: () => void;
  deselectAll: () => void;
  toggleViewMode: () => void;
  toggleSelectAll: () => void;
}

export const store = create<PhotoSelectionState>((set, store) => ({
  selectedIds: [],
  selectionMode: SelectionMode.DESELECT_ALL,
  viewMode: ViewMode.VIEW,
  selectId: (photoId) =>
    set((state) => ({
      ...state,
      selectedIds: Array.from(new Set([...state.selectedIds, photoId])),
    })),
  unSelectId: (photoId) =>
    set((state) => ({
      ...state,
      selectedIds: state.selectedIds.filter((id) => id !== photoId),
    })),
  selectAll: () =>
    set({
      selectedIds: photosStore.getState()._ids,
      selectionMode: SelectionMode.SELECT_ALL,
    }),
  deselectAll: () =>
    set({ selectedIds: [], selectionMode: SelectionMode.DESELECT_ALL }),
  toggleSelectAll: () => {
    if (store().selectionMode === SelectionMode.SELECT_ALL) {
      store().deselectAll();
    } else {
      store().selectAll();
    }
  },
  toggleViewMode: () => {
    store().deselectAll();
    set({
      viewMode:
        store().viewMode === ViewMode.VIEW ? ViewMode.SELECT : ViewMode.VIEW,
    });
  },
}));
